import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import api from "api/api";
import alertSignal from "signals/Alert.signal";
import loaderSignal from "signals/Loader.signal";
import { redirectToMsalLogin } from "libs/functions/global.functions";
import PublicAppContainer from "components/global/PublicAppContainer";
import Loader from "components/global/Loader";

const AcceptInvite = () => {
  const { instance } = useMsal();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token");

  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing invitation link.");
      return;
    }
    const fetchInvite = async () => {
      loaderSignal.update({ isContentLoading: true, message: "Loading invitation..." });
      try {
        const payload = await api.get({
          path: `/acceptInvite?token=${token}`,
          isUnAuthenticatedReq: true,
        });
        setInvite(
          payload && typeof payload === "object"
            ? {
                firstName: payload.firstName ?? "",
                lastName: payload.lastName ?? "",
                email: payload.email ?? "",
                organizationName: payload.organizationName ?? "",
                portal: payload.portal,
              }
            : null
        );
      } catch (err) {
        setError(err?.message || "Unable to load invitation.");
      } finally {
        loaderSignal.reset();
      }
    };
    fetchInvite();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    loaderSignal.update({ isContentLoading: true, message: "Creating account..." });
    setError(null);
    try {
      await api.post({
        path: "/acceptInvite",
        body: { token, password },
        isUnAuthenticatedReq: true,
      });
      alertSignal.update({
        type: "notification",
        variant: "success",
        message: "Account created successfully. You can now sign in.",
      });
      setInvite(null);
      setPassword("");
      setConfirmPassword("");
      setTimeout(() => redirectToMsalLogin(instance), 1500);
    } catch (err) {
      setError(err?.message || "Unable to accept invitation.");
    } finally {
      loaderSignal.reset();
    }
  };

  let content;
  if (!token || error) {
    content = (
      <Card className="mx-auto mt-80" style={{ maxWidth: "500px" }}>
        <Card.Body>
          <p className="text-center text-danger mb-0">
            {error || "Invalid or missing invitation link."}
          </p>
        </Card.Body>
      </Card>
    );
  } else if (!invite) {
    content = (
      <div className="d-flex justify-content-center align-items-center mt-80">
        <Loader message="Loading invitation..." />
      </div>
    );
  } else {
    content = (
      <Card className="mx-auto mt-80" style={{ maxWidth: "500px" }}>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="my-18">
              <Col className="text-center">
                <h4>Accept Invitation</h4>
              </Col>
            </Row>
            <p className="mb-2">
              <strong>Name:</strong>{" "}
              {[invite.firstName, invite.lastName].filter(Boolean).join(" ") ||
                invite.organizationName ||
                invite.email ||
                "—"}
            </p>
            <p className="mb-2">
              <strong>Email:</strong> {invite.email || "—"}
            </p>
            {invite.organizationName && (
              <p className="mb-4">
                <strong>Organization:</strong> {invite.organizationName}
              </p>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                placeholder="At least 8 characters"
              />
              <Form.Text className="text-muted">
                Password must be at least 8 characters and contain 3 of the following: uppercase letter, lowercase letter, digit, special character.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
              />
            </Form.Group>
            <div className="d-flex justify-content-end pt-16 border-top">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={loaderSignal.value.isContentLoading}
              >
                Create Account
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    );
  }

  return <PublicAppContainer>{content}</PublicAppContainer>;
};

export default AcceptInvite;
